/**
 * Game Plan Data Loader
 * 
 * Handles loading of game plan data with public/private filtering
 * and user-specific access control.
 */

import { supabase } from '@/integrations/supabase/client';
import { indexedDBService } from '../../indexeddb';
import { GamePlanTransformer } from '../DataTransformer';
import { BaseLoader, type LoaderResult } from './BaseLoader';

/**
 * Game Plan Data Loader Implementation
 * 
 * Specializes in loading game plans with proper access control,
 * filtering between user-created and public game plans based
 * on configuration settings.
 */
export class GamePlanDataLoader extends BaseLoader {
  /**
   * Load game plans for a user based on configuration
   */
  async load(userId: string): Promise<LoaderResult> {
    try {
      let query = supabase.from('game_plans').select('*');

      // Apply filtering based on configuration
      if (this.config.includeUserGamePlans && this.config.includePublicGamePlans) {
        // Load both user's game plans and public ones
        query = query.or(`user_id.eq.${userId},is_public.eq.true`);
      } else if (this.config.includeUserGamePlans) {
        // Load only user's game plans
        query = query.eq('user_id', userId);
      } else if (this.config.includePublicGamePlans) {
        // Load only public game plans
        query = query.eq('is_public', true);
      } else {
        // No game plans to load
        return this.createLoaderResult(0);
      }

      const { data, error } = await query.order('updated_at', { ascending: false });

      if (error) {
        return this.createLoaderResult(0, [error.message]);
      }

      if (data && data.length > 0) {
        const offlineGamePlans = data.map(GamePlanTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('game_plans', offlineGamePlans);
        return this.createLoaderResult(data.length);
      }

      return this.createLoaderResult(0);
    } catch (error) {
      const errorMessage = this.handleError(error, 'Game plans loading failed');
      return this.createLoaderResult(0, [errorMessage]);
    }
  }

  /**
   * Load only user's private game plans
   */
  async loadUserGamePlans(userId: string): Promise<LoaderResult> {
    try {
      const { data, error } = await supabase
        .from('game_plans')
        .select('*')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });

      if (error) {
        return this.createLoaderResult(0, [error.message]);
      }

      if (data && data.length > 0) {
        const offlineGamePlans = data.map(GamePlanTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('game_plans', offlineGamePlans);
        return this.createLoaderResult(data.length);
      }

      return this.createLoaderResult(0);
    } catch (error) {
      const errorMessage = this.handleError(error, 'User game plans loading failed');
      return this.createLoaderResult(0, [errorMessage]);
    }
  }

  /**
   * Load only public game plans
   */
  async loadPublicGamePlans(): Promise<LoaderResult> {
    try {
      const { data, error } = await supabase
        .from('game_plans')
        .select('*')
        .eq('is_public', true)
        .order('updated_at', { ascending: false });

      if (error) {
        return this.createLoaderResult(0, [error.message]);
      }

      if (data && data.length > 0) {
        const offlineGamePlans = data.map(GamePlanTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('game_plans', offlineGamePlans);
        return this.createLoaderResult(data.length);
      }

      return this.createLoaderResult(0);
    } catch (error) {
      const errorMessage = this.handleError(error, 'Public game plans loading failed');
      return this.createLoaderResult(0, [errorMessage]);
    }
  }

  /**
   * Load game plan templates
   */
  async loadTemplates(): Promise<LoaderResult> {
    try {
      const { data, error } = await supabase
        .from('game_plans')
        .select('*')
        .eq('is_template', true)
        .order('updated_at', { ascending: false });

      if (error) {
        return this.createLoaderResult(0, [error.message]);
      }

      if (data && data.length > 0) {
        const offlineGamePlans = data.map(GamePlanTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('game_plans', offlineGamePlans);
        return this.createLoaderResult(data.length);
      }

      return this.createLoaderResult(0);
    } catch (error) {
      const errorMessage = this.handleError(error, 'Game plan templates loading failed');
      return this.createLoaderResult(0, [errorMessage]);
    }
  }

  /**
   * Load admin templates
   */
  async loadAdminTemplates(): Promise<LoaderResult> {
    try {
      const { data, error } = await supabase
        .from('game_plans')
        .select('*')
        .eq('is_admin_template', true)
        .order('updated_at', { ascending: false });

      if (error) {
        return this.createLoaderResult(0, [error.message]);
      }

      if (data && data.length > 0) {
        const offlineGamePlans = data.map(GamePlanTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('game_plans', offlineGamePlans);
        return this.createLoaderResult(data.length);
      }

      return this.createLoaderResult(0);
    } catch (error) {
      const errorMessage = this.handleError(error, 'Admin templates loading failed');
      return this.createLoaderResult(0, [errorMessage]);
    }
  }

  /**
   * Load game plans by specific criteria
   */
  async loadByFilter(filter: {
    userId?: string;
    isPublic?: boolean;
    isTemplate?: boolean;
    isAdminTemplate?: boolean;
  }): Promise<LoaderResult> {
    try {
      let query = supabase.from('game_plans').select('*');

      if (filter.userId) {
        query = query.eq('user_id', filter.userId);
      }
      if (filter.isPublic !== undefined) {
        query = query.eq('is_public', filter.isPublic);
      }
      if (filter.isTemplate !== undefined) {
        query = query.eq('is_template', filter.isTemplate);
      }
      if (filter.isAdminTemplate !== undefined) {
        query = query.eq('is_admin_template', filter.isAdminTemplate);
      }

      const { data, error } = await query.order('updated_at', { ascending: false });

      if (error) {
        return this.createLoaderResult(0, [error.message]);
      }

      if (data && data.length > 0) {
        const offlineGamePlans = data.map(GamePlanTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('game_plans', offlineGamePlans);
        return this.createLoaderResult(data.length);
      }

      return this.createLoaderResult(0);
    } catch (error) {
      const errorMessage = this.handleError(error, 'Filtered game plans loading failed');
      return this.createLoaderResult(0, [errorMessage]);
    }
  }

  /**
   * Get game plan statistics for a user
   */
  async getGamePlanStats(userId: string): Promise<{
    totalGamePlans: number;
    publicGamePlans: number;
    privateGamePlans: number;
    templates: number;
    lastUpdated?: string;
  }> {
    try {
      const gamePlans = await indexedDBService.gamePlans.getByUserId(userId);
      
      const totalGamePlans = gamePlans.length;
      const publicGamePlans = gamePlans.filter(gp => gp.is_public === 1).length;
      const privateGamePlans = gamePlans.filter(gp => gp.is_public === 0).length;
      const templates = gamePlans.filter(gp => gp.is_template === 1).length;
      
      const lastUpdated = gamePlans.length > 0 ? 
        gamePlans.reduce((latest, gp) => 
          gp.updated_at > latest ? gp.updated_at : latest, gamePlans[0].updated_at
        ) : undefined;

      return {
        totalGamePlans,
        publicGamePlans,
        privateGamePlans,
        templates,
        lastUpdated
      };
    } catch (error) {
      console.warn('Failed to calculate game plan stats:', error);
      return {
        totalGamePlans: 0,
        publicGamePlans: 0,
        privateGamePlans: 0,
        templates: 0
      };
    }
  }

  /**
   * Check if user has access to a specific game plan
   */
  async hasAccess(userId: string, gamePlanId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('game_plans')
        .select('user_id, is_public')
        .eq('id', gamePlanId)
        .single();

      if (error || !data) {
        return false;
      }

      // User has access if they own it or it's public
      return data.user_id === userId || data.is_public === true;
    } catch (error) {
      console.warn('Failed to check game plan access:', error);
      return false;
    }
  }
}

// Export singleton instance
export const gamePlanDataLoader = new GamePlanDataLoader();
